import React, { useEffect, useState } from "react"
import cx from "classnames"
import { withOrientationChange } from "react-device-detect"
import useWindowResize from "@/hooks/useWindowResize"
import { getShortenedUrl, sanitizeText } from "@/utils/helper"
import { getEventInfo } from "@/components/InPageSearch/v1/analytics"
import Button from "@/components/core/Button/Button"
import PartsViewer from "@/components/PartsViewer/v1/PartsViewer"
import PartsViewerV2 from "@/components/PartsViewer/v2/PartsViewer"
import CONSTANTS, {
  inPageSearchEndPoint,
  LOCALE_CANADA_FR,
  VISUAL_SEARCH_ALLOWED_URLS,
} from "@/constants/index"
import inPageSearchStyle from "@/components/InPageSearch/v1/index.module.scss"
import { apim } from "@/constants/api"
import _get from "lodash/get"
import qs from "qs"
import VisualSearch from "@/components/VisualSearch/v1"
import useViewport from "@/hooks/useViewport"
import {
  VisualSearchIconNew,
  VisualSearchIconNewMobile,
  CrossIcon,
} from "@/components/InPageSearch/v1/icons"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"
import { addDataLayerVisualSearch } from "@/components/VisualSearch/analytics"
import { useTranslation } from "next-i18next"
import { getTranslations } from "@/components/VisualSearch/localization"
import { initMedallia } from "@/services/medallia"
import { MEDALLIA_FORM_ID } from "@/components/InPageSearch/v1/utils"

const InPageSearchView = props => {
  const {
    bgColor = "",
    loading = false,
    partId = "",
    isSkuSearch = false,
    productId = "",
    searchString = "",
    pdfPartsDiagram = "",
    ctaLink = "",
    ctaLabel = "",
    type = "",
    onChange = () => {},
    title = "",
    lwAppName = "",
    language = "en",
    svgFileMap = [],
    searchType = "",
    placeholder = "",
    showVisualSearchicon = true,
  } = props

  const device = useViewport()
  const { featureFlags } = useSelector(selectGenericState)
  const {
    MYSQLCONNSTR_ENABLE_PARTS_VIEWER_V2: isPartsViewerV2Enabled = false,
    MYSQLCONNSTR_ENABLE_VISUAL_SEARCH_V1: isVisualSearchEnabled = false,
  } = featureFlags

  const [width] = useWindowResize()
  const isDarkBg = bgColor === "bgDark"
  const [isShowModal, setIsShowModal] = useState(false)
  const [shortenedUrl, setShortenedUrl] = useState(ctaLink)
  const [showVisualSearch, setShowVisualSearch] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const { t } = useTranslation()
  const { searchText, searchImageText } = getTranslations(t)

  const {
    globalConfig: {
      internationalization: { locale = LOCALE_CANADA_FR } = {},
    } = {},
  } = useSelector(selectGenericState)

  const isServicePage = VISUAL_SEARCH_ALLOWED_URLS.includes(
    window.location.pathname
  )

  const VSGreyIcon = device.isSmallScreen ? (
    <VisualSearchIconNewMobile />
  ) : (
    <VisualSearchIconNew />
  )
  const visualSearchIcon = VSGreyIcon
  useEffect(() => {
    if (ctaLink) {
      getShortenedUrl(ctaLink).then(url => setShortenedUrl(url))
    }
  }, [ctaLink])

  useEffect(() => {
    initMedallia(MEDALLIA_FORM_ID)
  }, [showVisualSearch])

  const handleKeyPress = event => {
    const { keyCode = "", key = "" } = event
    if (keyCode === CONSTANTS.KEYCODE.ENTER && key === CONSTANTS.KEY.ENTER) {
      if (searchString !== "" && searchString.length <= 3) {
        location.href = `${
          locale === LOCALE_CANADA_FR
            ? shortenedUrl.replace?.("/en/", "")
            : shortenedUrl
        }?keyword=${searchString}${
          searchType === "parts" ? "&service=true" : ""
        }`
      } else {
        const fl =
          "id,sku_s,slug_s,ProductResource.InteractiveSVG.resourceName_ss,ProductProductNo_s,ProductResource.InteractiveSVG.resourceTitle_ss,ProductResource.PartsPDFFileName.resourceFullWebURL_ss"
        apim
          .get(inPageSearchEndPoint, {
            params: {
              q: searchString,
              fl,
              fq: ['Products:("ct")', `language_s:("${language}")`],
              collections: lwAppName.toLowerCase(),
              profilename: `profile_${lwAppName.toLowerCase()}_Search`,
            },
            paramsSerializer: params => {
              return qs.stringify(params, {
                arrayFormat: "repeat",
                encode: false,
              })
            },
          })
          .then(res => {
            const {
              data: {
                response: { numFound = "", docs = [] } = {},
                fusion: { exactMatch = "" } = {},
              } = {},
            } = res
            if (numFound === 1 && exactMatch) {
              const mResources = _get(
                docs[0],
                "ProductResource.InteractiveSVG.resourceName_ss",
                ""
              )
              const partId = Array.isArray(mResources)
                ? mResources[0]
                : mResources

              const productId = _get(docs[0], "ProductProductNo_s", "")
              setIsShowModal(Boolean(partId) && Boolean(productId))
            } else {
              location.href = `${
                locale === LOCALE_CANADA_FR
                  ? shortenedUrl.replace?.("/en/", "")
                  : shortenedUrl
              }?keyword=${searchString}${
                searchType === "parts" ? "&service=true" : ""
              }`
            }
          })
          .catch(err => console.log("error occured in fetch search term", err))
      }
    }
  }

  const handleOnClick = event => {
    event?.preventDefault()

    if (Boolean(partId) && Boolean(productId)) {
      setIsShowModal(true)
    }

    if (pdfPartsDiagram && pdfPartsDiagram[0]) {
      handlePDFDownload(event, pdfPartsDiagram ? pdfPartsDiagram[0] : "")
    }
  }

  const handlePDFDownload = (e, link) => {
    e.stopPropagation()

    try {
      window.open(link.trim(), "_blank")
    } catch (err) {
      console.log(err)
    }
  }

  const onCloseModal = () => {
    setIsShowModal(false)
  }

  const getButton = () => {
    if (isSkuSearch) {
      return (
        <div className={inPageSearchStyle?.inPageSearchWrapper}>
          <button
            className={`kf-react-button button gbh-data-layer large ${
              isDarkBg ? "secondary" : "ghost"
            } ${loading ? "disabled" : " "} ${
              searchString && searchString !== null && searchString.length > 0
                ? "--active"
                : "--inactive"
            }`}
            data-gbh-data-layer={getEventInfo(
              `${ctaLink}?keyword=${searchString}`,
              "success"
            )}
            onClick={handleOnClick}
          >
            {ctaLabel}
          </button>
        </div>
      )
    } else {
      return (
        <Button
          label={ctaLabel}
          type={isDarkBg ? "secondary" : "ghost"}
          to={`${
            locale === LOCALE_CANADA_FR
              ? shortenedUrl.replace?.("/en/", "")
              : shortenedUrl
          }?keyword=${searchString}${
            searchType === "parts" ? "&service=true" : ""
          }`}
          className={`gbh-data-layer ${loading ? "disabled" : " "} ${
            searchString && searchString !== null && searchString.length > 0
              ? "--active"
              : "--inactive"
          }`}
          data-gbh-data-layer={getEventInfo(
            `${ctaLink}?keyword=${searchString}`,
            "error",
            type,
            searchString
          )}
        />
      )
    }
  }

  useEffect(() => {
    const getSearchKey = window.localStorage.getItem("getSearchKey")
    setShowTooltip(Boolean(!getSearchKey))
  }, [])

  const showVSDiv = e => {
    e.preventDefault()
    e.stopPropagation()
    addDataLayerVisualSearch({
      eventKey: "search using an image",
    })
    setShowVisualSearch(i => !i)
  }

  const hideTooltip = e => {
    e.preventDefault()
    e.stopPropagation()
    setShowTooltip(false)
    window.localStorage.setItem("getSearchKey", true)
    document
      .querySelector(".tool-tip-clickable")
      ?.classList.add("tool-tip-clickable-hide")
  }
  return (
    <div
      className={`in-page-search ${
        showVisualSearch ? "show-visualsearch-wrapper" : ""
      }`}
    >
      <div
        className={cx(
          "inpage-search kf-react-container",
          isDarkBg && "inpage-search--dark"
        )}
      >
        <div className="container">
          <div className="row inpage-search-section">
            <div
              className={cx(
                "col-12 inpage-search__title",
                isDarkBg && "inpage-search__title--dark"
              )}
            >
              {sanitizeText(title)}
            </div>
            <div className="col-12 inpage-search__header">
              <div
                className={cx(
                  "inpage-search__header-section",
                  !isDarkBg && "inpage-search__header-section--light"
                )}
              >
                <div className="inpage-search__search-filed ">
                  <label
                    className="inpage-search__label"
                    htmlFor="inpage-search__search-control"
                  >
                    <input
                      autoComplete="off"
                      type="text"
                      className={cx(
                        "inpage-search__search-control",
                        isDarkBg && "inpage-search__search-control--dark"
                      )}
                      id="inpage-search__search-control"
                      placeholder={placeholder}
                      aria-labelledby="searchBox"
                      onChange={e => onChange(e.target.value)}
                      onKeyUp={e => handleKeyPress(e)}
                    />

                    {isVisualSearchEnabled &&
                    showVisualSearchicon &&
                    isServicePage ? (
                      <div
                        className={`inpage-search__search-visual-icon ${
                          showVisualSearch
                            ? "inpage-search__search-visual_show"
                            : "inpage-search__search-visual"
                        }`}
                        onClick={e => showVSDiv(e)}
                      >
                        {visualSearchIcon}
                        {showTooltip && (
                          <div className="tool-tip-clickable">
                            <p className="text-search">{searchText}</p>
                            <span
                              className="cross-icon-wrapper  mb-3"
                              onClick={e => hideTooltip(e)}
                            >
                              <CrossIcon />
                            </span>
                          </div>
                        )}

                        {device?.isLargeTabView &&
                          !showVisualSearch &&
                          !showTooltip && (
                            <span className="tool-tip-custom">
                              {searchImageText}
                            </span>
                          )}
                      </div>
                    ) : null}
                  </label>
                </div>
                {width > 480 &&
                  searchString &&
                  searchString !== null &&
                  searchString.length > 0 &&
                  getButton()}
                {isPartsViewerV2Enabled ? (
                  <PartsViewerV2
                    page="search"
                    onClose={onCloseModal}
                    showModal={isShowModal}
                    productId={productId}
                    partId={partId}
                    fileMap={svgFileMap.map(val => {
                      return { id: val.url, ...val }
                    })}
                  />
                ) : (
                  <PartsViewer
                    productId={productId}
                    partId={partId}
                    showModal={isShowModal}
                    page="search"
                    onClose={onCloseModal}
                  />
                )}
              </div>
              {width <= 480 && getButton()}
            </div>
          </div>
        </div>
      </div>
      {showVisualSearch ? (
        <VisualSearch showVisualSearch={showVisualSearch} />
      ) : null}
    </div>
  )
}

export default withOrientationChange(InPageSearchView)
